import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { api, formatMoney } from "@/lib/api";
import { CheckCircle2, XCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refresh } = useAuth();
  const [status, setStatus] = useState({ payment_status: null, amount: 0, currency: "usd" });
  const [done, setDone] = useState(false);
  const attempts = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { navigate("/"); return; }

    let active = true;

    const poll = async () => {
      if (!active) return;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        setStatus({ payment_status: data.payment_status, amount: data.amount_total / 100, currency: data.currency });
        if (data.payment_status === "paid" || data.status === "expired") {
          setDone(true);
          if (data.payment_status === "paid") await refresh();
          return;
        }
      } catch {}
      attempts.current += 1;
      if (attempts.current >= 8) { setDone(true); return; }
      setTimeout(poll, 2000);
    };
    poll();

    return () => { active = false; };
    // eslint-disable-next-line
  }, []);

  const paid = status.payment_status === "paid";

  return (
    <main data-testid="payment-success-page" className="min-h-screen flex items-center justify-center px-6">
      <div className="w-full max-w-lg text-center lux-card p-14">
        {!done ? (
          <>
            <div className="font-display text-3xl gold-text shimmer-text">Verifying payment…</div>
            <div className="text-sm text-secondary-fg mt-3">Please wait while we confirm the transaction.</div>
          </>
        ) : paid ? (
          <>
            <CheckCircle2 className="mx-auto text-gold" size={56} strokeWidth={1} />
            <div data-testid="payment-success-message" className="font-display text-4xl mt-6">Payment received.</div>
            <div className="font-display text-6xl gold-text mt-6 tracking-tighter">{formatMoney(status.amount, status.currency)}</div>
            <div className="text-sm text-secondary-fg mt-3">A discreet thank you from y ℶα.</div>
            <div className="mt-10 flex justify-center gap-3">
              <Link to={user ? (user.role === "merchant" ? "/merchant" : "/customer") : "/"} className="btn-gold">
                {user ? "Return to dashboard" : "Back home"}
              </Link>
            </div>
          </>
        ) : (
          <>
            <XCircle className="mx-auto text-red-400" size={56} strokeWidth={1} />
            <div data-testid="payment-failed-message" className="font-display text-3xl mt-6">Payment not completed</div>
            <div className="text-sm text-secondary-fg mt-3">The session expired or was cancelled.</div>
            <div className="mt-10">
              <Link to={user ? (user.role === "merchant" ? "/merchant" : "/customer") : "/"} className="btn-ghost">
                Go back
              </Link>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
